// src/features/tasks/hooks/useTaskActions.js
import { useState, useCallback } from 'react';
import { useTasks } from './useTasks';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import {
  executeTaskStateChange,
  getActionPastTense,
  requiresConfirmation,
  getConfirmationConfig,
} from '../utils/taskStateUtils';
import { tasksAPI } from '../services/tasks';
import { taskEquipmentAPI } from '../services/taskEquipment';
import i18n from '../../../i18n';

/** Format a Date as a plain YYYY-MM-DD day, without shifting it through UTC */
function toApiDate(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function useTaskActions({ onSuccess, refreshTasks } = {}) {
  const { showSuccess, showError } = useSnackbar();
  const {
    startTask,
    putTaskOnHold,
    resumeTask,
    cancelTask,
    completeTask,
    markTaskNotCompleted,
    reopenTask,
  } = useTasks();

  // Dialog states
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [currentTask, setCurrentTask] = useState(null);
  const [notes, setNotes] = useState('');
  const [lastCompletion, setLastCompletion] = useState(null);
  // Backdated completion — only set when the user opens the date control
  const [completionDate, setCompletionDate] = useState(null);
  const [completionDateReason, setCompletionDateReason] = useState('');
  // Rotating equipment left unchecked when completing: { task, action, links }
  const [equipmentWarning, setEquipmentWarning] = useState(null);
  const [equipmentWarningBusy, setEquipmentWarningBusy] = useState(false);

  // Opens the confirmation (or runs the action straight away)
  const proceedWithStateChange = useCallback(async (task, action) => {
    try {
      setCurrentTask(task);

      if (requiresConfirmation(action)) {
        setPendingAction(action);
        setLastCompletion(null);
        try {
          const res = await tasksAPI.getLastCompletion(task.task_id);
          setLastCompletion(res.data?.data ?? null);
        } catch {
          // No parent task or no history — silently ignore
        }
        setConfirmDialogOpen(true);
        return;
      }

      await executeStateChangeAction(task.task_id, action);
    } catch (error) {
      showError(i18n.t('tasks:errors.state_change_failed'));
    }
  }, []);

  // Completing with rotating equipment unchecked means those values don't
  // move on, so ask first
  const handleStateChange = useCallback(async (task, action) => {
    if (action === 'complete') {
      try {
        const res = await taskEquipmentAPI.listTaskEquipment(task.task_id);
        const links = res.data?.data?.relationships || [];
        // The dialog shows all the task's equipment, like the Opgaveudstyr card
        if (links.some(link => link.rotation_options?.length > 0 && !link.is_checked)) {
          setEquipmentWarning({ task, action, links });
          return;
        }
      } catch {
        // Can't tell; don't block completing the task over it
      }
    }
    await proceedWithStateChange(task, action);
  }, [proceedWithStateChange]);

  const handleCancelEquipmentWarning = useCallback(() => setEquipmentWarning(null), []);

  // Save the checked state chosen in the dialog (only what changed), then
  // carry on completing
  const handleCheckAndContinue = useCallback(async (checkedIds) => {
    const { task, action, links } = equipmentWarning;
    const wanted = new Set(checkedIds);
    const changes = links.filter(link => Boolean(link.is_checked) !== wanted.has(link.task_equipment_id));
    if (changes.length > 0) {
      setEquipmentWarningBusy(true);
      try {
        await Promise.all(changes.map(link => (
          taskEquipmentAPI.setTaskEquipmentChecked(task.task_id, link.task_equipment_id, wanted.has(link.task_equipment_id))
        )));
      } catch {
        showError(i18n.t('tasks:equipment_warning.check_failed'));
        setEquipmentWarningBusy(false);
        return;
      }
      setEquipmentWarningBusy(false);
      refreshTasks?.();
    }
    setEquipmentWarning(null);
    await proceedWithStateChange(task, action);
  }, [equipmentWarning, proceedWithStateChange, refreshTasks, showError]);

  // Execute the actual state change
  const executeStateChangeAction = useCallback(async (taskId, action, actionNotes = '', backdate = null) => {
    try {
      const taskAPI = {
        startTask,
        putTaskOnHold,
        resumeTask,
        completeTask: (id) => completeTask(id, {
          completion_notes: actionNotes,
          ...(backdate?.date ? {
            completion_date: toApiDate(backdate.date),
            completion_date_reason: backdate.reason,
          } : {}),
        }),
        markTaskNotCompleted: (id) => markTaskNotCompleted(id, { reason: actionNotes }),
        cancelTask,
        reopenTask,
      };

      const result = await executeTaskStateChange(taskId, action, taskAPI);

      if (result.success) {
        const pastTense = getActionPastTense(action);
        showSuccess(i18n.t('tasks:success.state_action', { action: pastTense }));
        onSuccess?.(result.data);
        refreshTasks?.();
      } else {
        showError(result.error || i18n.t('tasks:errors.state_change_failed'));
      }

      return result;
    } catch (error) {
      showError(i18n.t('tasks:errors.state_change_failed'));
    }
  }, [startTask, putTaskOnHold, resumeTask, completeTask, markTaskNotCompleted, cancelTask, reopenTask, showSuccess, showError, onSuccess, refreshTasks]);

  // Dialog handlers
  const handleConfirmAction = useCallback(async () => {
    if (pendingAction && currentTask) {
      await executeStateChangeAction(
        currentTask.task_id,
        pendingAction,
        notes,
        completionDate ? { date: completionDate, reason: completionDateReason.trim() } : null,
      );
      setPendingAction(null);
      setCurrentTask(null);
      setConfirmDialogOpen(false);
      setNotes('');
      setCompletionDate(null);
      setCompletionDateReason('');
    }
  }, [pendingAction, currentTask, notes, completionDate, completionDateReason, executeStateChangeAction]);

  const handleCloseConfirmDialog = useCallback(() => {
    setConfirmDialogOpen(false);
    setPendingAction(null);
    setCurrentTask(null);
    setNotes('');
    setLastCompletion(null);
    setCompletionDate(null);
    setCompletionDateReason('');
  }, []);

  const confirmConfig = pendingAction ? getConfirmationConfig(pendingAction) : null;
  const isConfirmDisabled = Boolean(
    (confirmConfig?.notesField?.required && !notes.trim()) ||
    (completionDate && !completionDateReason.trim())
  );

  return {
    handleStateChange,

    dialogStates: {
      confirmDialogOpen,
      notes,
      currentTask,
      confirmConfig,
      isConfirmDisabled,
      lastCompletion,
      completionDate,
      completionDateReason,
      equipmentWarning,
      equipmentWarningBusy,
    },

    dialogHandlers: {
      handleCancelEquipmentWarning,
      handleCheckAndContinue,
      handleConfirmAction,
      handleCloseConfirmDialog,
      setNotes,
      setCompletionDate,
      setCompletionDateReason,
    },
  };
}